import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  BILLING_FREQUENCIES,
  type BillingFrequency,
} from "@/types/component-price";
import {
  useComponentPrice,
  useCreateComponentPrice,
  useUpdateComponentPrice,
} from "@/hooks/useComponentPrices";
import { CURRENCY_METADATA } from "@/lib/currency";
import { useCloseGuard } from "@/hooks/use-close-guard";

// ============================================================================
// Currency List — derived from shared CURRENCY_METADATA
// ============================================================================

const CURRENCIES = Object.entries(CURRENCY_METADATA).map(
  ([code, { name, symbol }]) => ({ code, name, symbol }),
);

// ============================================================================
// Validation Schema
// ============================================================================

const formSchema = z
  .object({
    region: z.string().optional(),
    pricePerUnit: z.coerce.number().min(0, "Price must be >= 0"),
    currency: z.string().min(3, "Currency is required").max(3),
    billingFrequency: z.enum(BILLING_FREQUENCIES),
    minQuantity: z.coerce
      .number()
      .int("Must be a whole number")
      .min(0)
      .optional()
      .nullable(),
    maxQuantity: z.coerce
      .number()
      .int("Must be a whole number")
      .optional()
      .nullable(),
    effectiveFrom: z.string().min(1, "Effective From is required"),
    effectiveTo: z.string().optional(),
    isActive: z.boolean().default(true),
    metadata: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val ||
          (() => {
            try {
              JSON.parse(val);
              return true;
            } catch {
              return false;
            }
          })(),
        "Invalid JSON format",
      ),
  })
  .refine(
    (data) =>
      !data.maxQuantity ||
      !data.minQuantity ||
      data.maxQuantity > data.minQuantity,
    {
      message: "Max quantity must be greater than min quantity",
      path: ["maxQuantity"],
    },
  )
  .refine(
    (data) =>
      !data.effectiveTo ||
      new Date(data.effectiveTo) > new Date(data.effectiveFrom),
    {
      message: "Effective To must be after Effective From",
      path: ["effectiveTo"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

// ============================================================================
// Component Props
// ============================================================================

interface ComponentPriceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  componentId: string;
  componentName: string;
  priceId?: string;
  mode: "create" | "edit";
}

// ============================================================================
// Component
// ============================================================================

export function ComponentPriceForm({
  open,
  onOpenChange,
  componentId,
  componentName,
  priceId,
  mode,
}: ComponentPriceFormProps) {
  const createMutation = useCreateComponentPrice();
  const updateMutation = useUpdateComponentPrice();

  const isEdit = mode === "edit";
  const { data: detail, isLoading: isLoadingDetail } = useComponentPrice(
    isEdit ? priceId : undefined,
  );

  const isDetailLoaded = !isEdit || !!detail;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      region: "",
      pricePerUnit: 0,
      currency: "VND",
      billingFrequency: "Monthly" as BillingFrequency,
      minQuantity: null,
      maxQuantity: null,
      effectiveFrom: new Date().toISOString(),
      effectiveTo: "",
      isActive: true,
      metadata: "",
    },
    values:
      isEdit && detail
        ? {
            region: detail.region || "",
            pricePerUnit: detail.pricePerUnit,
            currency: detail.currency,
            billingFrequency: detail.billingFrequency,
            minQuantity: detail.minQuantity ?? null,
            maxQuantity: detail.maxQuantity ?? null,
            effectiveFrom: detail.effectiveFrom,
            effectiveTo: detail.effectiveTo || "",
            isActive: detail.isActive,
            metadata: detail.metadata || "",
          }
        : undefined,
  });

  const onSubmit = async (data: FormValues) => {
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({
          componentId,
          data: {
            region: data.region || undefined,
            pricePerUnit: data.pricePerUnit,
            currency: data.currency,
            billingFrequency: data.billingFrequency,
            minQuantity: data.minQuantity ?? undefined,
            maxQuantity: data.maxQuantity ?? undefined,
            effectiveFrom: data.effectiveFrom,
            effectiveTo: data.effectiveTo || undefined,
            metadata: data.metadata || undefined,
          },
        });
      } else if (priceId) {
        await updateMutation.mutateAsync({
          id: priceId,
          data: {
            pricePerUnit: data.pricePerUnit,
            effectiveTo: data.effectiveTo || undefined,
            isActive: data.isActive,
            metadata: data.metadata || undefined,
          },
        });
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit Component Price" : "Create Component Price"}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              {isEdit
                ? `Update the price details for ${componentName}.`
                : `Create a new price for ${componentName}.`}
            </DialogDescription>
            {isEdit && !isDetailLoaded ? (
              <div className="py-12 text-center text-muted-foreground">
                {isLoadingDetail
                  ? "Loading price details..."
                  : "Price not found"}
              </div>
            ) : (
              <Form {...form}>
                <form
                  id="component-price-form"
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {/* Region Field */}
                  <FormField
                    control={form.control}
                    name="region"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Region (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., US-East-1, EU-West-1, or leave empty for global"
                            {...field}
                            disabled={isSubmitting || isEdit}
                          />
                        </FormControl>
                        <FormDescription>
                          {isEdit
                            ? "Region cannot be changed after creation"
                            : "Specify a region for regional pricing, or leave empty for global pricing"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Price and Currency Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="pricePerUnit"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price Per Unit *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              {...field}
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormDescription>Price for one unit</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currency *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={isSubmitting || isEdit}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select currency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CURRENCIES.map((curr) => (
                                <SelectItem key={curr.code} value={curr.code}>
                                  {curr.code} - {curr.name} ({curr.symbol})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            {isEdit
                              ? "Currency cannot be changed after creation"
                              : "Currency for this price"}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Billing Frequency Field */}
                  <FormField
                    control={form.control}
                    name="billingFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Billing Frequency *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isSubmitting || isEdit}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select billing frequency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {BILLING_FREQUENCIES.map((freq) => (
                              <SelectItem key={freq} value={freq}>
                                {freq}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          {isEdit
                            ? "Billing frequency cannot be changed after creation"
                            : "How often this component is billed"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Quantity Range Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="minQuantity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Min Quantity</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="Optional"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value);
                                field.onChange(value);
                              }}
                              disabled={isSubmitting || isEdit}
                            />
                          </FormControl>
                          <FormDescription>
                            Minimum quantity for this price
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="maxQuantity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Max Quantity</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Optional"
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value);
                                field.onChange(value);
                              }}
                              disabled={isSubmitting || isEdit}
                            />
                          </FormControl>
                          <FormDescription>
                            Maximum quantity for this price
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Date Range Picker */}
                  <FormField
                    control={form.control}
                    name="effectiveFrom"
                    render={() => (
                      <FormItem>
                        <FormLabel>Effective Date Range *</FormLabel>
                        <FormControl>
                          <DateRangePicker
                            value={{
                              from: form.watch("effectiveFrom"),
                              to: form.watch("effectiveTo") || undefined,
                            }}
                            onChange={(value) => {
                              if (value.from) {
                                form.setValue("effectiveFrom", value.from);
                              }
                              if (value.to !== undefined) {
                                form.setValue("effectiveTo", value.to || "");
                              }
                            }}
                            disabled={isSubmitting || isEdit}
                            placeholder="Select date range"
                          />
                        </FormControl>
                        <FormDescription>
                          {isEdit
                            ? "Effective From cannot be changed. You can only modify Effective To"
                            : "Date range when this price is valid"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Status Toggle (Edit mode only) */}
                  {isEdit && (
                    <FormField
                      control={form.control}
                      name="isActive"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">
                              Active Status
                            </FormLabel>
                            <FormDescription>
                              Inactive prices will not be used for billing
                              calculations
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isSubmitting}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Metadata Field (Optional) */}
                  <FormField
                    control={form.control}
                    name="metadata"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Metadata (JSON)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder='{"key": "value"}'
                            className="resize-none font-mono text-sm"
                            rows={3}
                            {...field}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional JSON metadata for custom properties
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
            )}
          </DialogBody>
          {/* Outside the form so they stay in view; `form=` ties the submit button back to it. */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="component-price-form"
              disabled={isSubmitting || (isEdit && !isDetailLoaded)}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                  ? "Update Price"
                  : "Create Price"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
